import { useMemo, useState } from 'react'
import { Check, Plus, X, Upload } from 'lucide-react'
import {
  useOnboardingStatusQuery,
  useOnboardingChangePasswordMutation,
  useOnboardingBrandingMutation,
  useOnboardingIntegrationsMutation,
  useOnboardingCompleteMutation,
  type CredentialInput,
} from './onboarding-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type StepKey = 'password' | 'branding' | 'integrations' | 'finish'

export function OnboardingGate() {
  const { data, isLoading } = useOnboardingStatusQuery()
  const status = data?.data

  const steps = useMemo<StepKey[]>(() => {
    if (!status) return []
    const s: StepKey[] = []
    if (!status.steps.password) s.push('password')
    s.push('branding', 'integrations', 'finish')
    return s
  }, [status])

  const [idx, setIdx] = useState(0)

  // password
  const [cur, setCur] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [changePassword, { isLoading: changingPw, error: pwError }] = useOnboardingChangePasswordMutation()

  // branding
  const [logo, setLogo] = useState<File | null>(null)
  const [primary, setPrimary] = useState('#1F4E79')
  const [secondary, setSecondary] = useState('#FFA500')
  const [saveBranding, { isLoading: savingBrand }] = useOnboardingBrandingMutation()

  // integrations
  const [creds, setCreds] = useState<CredentialInput[]>([{ group: '', key: '', value: '' }])
  const [saveIntegrations, { isLoading: savingInt }] = useOnboardingIntegrationsMutation()

  const [complete, { isLoading: completing }] = useOnboardingCompleteMutation()

  if (isLoading || !status || !status.needs_onboarding || steps.length === 0) return null

  const step = steps[Math.min(idx, steps.length - 1)]
  const next = () => setIdx((i) => Math.min(i + 1, steps.length - 1))

  async function submitPassword() {
    if (!cur || !pw || pw !== pw2) return
    try {
      await changePassword({ current_password: cur, new_password: pw, new_password_confirmation: pw2 }).unwrap()
      next()
    } catch {
      /* error shown inline */
    }
  }

  async function submitBranding() {
    const fd = new FormData()
    if (logo) fd.append('logo', logo)
    if (primary) fd.append('brand_primary_color', primary)
    if (secondary) fd.append('brand_secondary_color', secondary)
    try {
      await saveBranding(fd).unwrap()
    } catch {
      /* ignore */
    }
    next()
  }

  async function submitIntegrations() {
    const credentials = creds.filter((c) => c.group.trim() && c.key.trim())
    if (credentials.length > 0) {
      try {
        await saveIntegrations({ credentials }).unwrap()
      } catch {
        /* ignore */
      }
    }
    next()
  }

  async function finish() {
    try {
      await complete().unwrap()
    } catch {
      /* status refetch will re-evaluate */
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl border bg-background p-6 shadow-xl">
        <div className="mb-4">
          <h2 className="font-display text-2xl font-semibold">Welcome to {status.company.name}</h2>
          <p className="text-sm text-muted-foreground">Let's get your workspace set up.</p>
        </div>

        {/* progress */}
        <div className="mb-6 flex items-center gap-2">
          {steps.map((sname, i) => (
            <div key={sname} className={'h-1.5 flex-1 rounded-full ' + (i <= idx ? 'bg-primary' : 'bg-muted')} />
          ))}
        </div>

        {step === 'password' && (
          <div className="space-y-4">
            <h3 className="font-medium">Set a new password</h3>
            <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
            {pw && pw2 && pw !== pw2 && <p className="text-xs text-destructive">Passwords do not match.</p>}
            {pwError ? <p className="text-xs text-destructive">Could not change password. Check your current password and that the new one is strong enough.</p> : null}
            <p className="text-xs text-muted-foreground">Use at least 8 characters with upper &amp; lower case, a number and a symbol.</p>
            <div className="flex justify-end">
              <Button disabled={changingPw || !cur || !pw || pw !== pw2} onClick={() => void submitPassword()}>
                {changingPw ? 'Saving…' : 'Set password'}
              </Button>
            </div>
          </div>
        )}

        {step === 'branding' && (
          <div className="space-y-4">
            <h3 className="font-medium">Branding <span className="text-xs font-normal text-muted-foreground">(optional)</span></h3>
            <div className="space-y-1.5">
              <Label>Logo</Label>
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-muted">
                <Upload className="h-4 w-4" />
                {logo ? logo.name : 'Upload a logo image'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setLogo(e.target.files?.[0] ?? null)} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Primary color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="h-9 w-12 rounded border" />
                  <Input value={primary} onChange={(e) => setPrimary(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Secondary color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={secondary} onChange={(e) => setSecondary(e.target.value)} className="h-9 w-12 rounded border" />
                  <Input value={secondary} onChange={(e) => setSecondary(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={next}>Skip</Button>
              <Button disabled={savingBrand} onClick={() => void submitBranding()}>{savingBrand ? 'Saving…' : 'Save & continue'}</Button>
            </div>
          </div>
        )}

        {step === 'integrations' && (
          <div className="space-y-4">
            <h3 className="font-medium">Integration credentials <span className="text-xs font-normal text-muted-foreground">(optional)</span></h3>
            <p className="text-xs text-muted-foreground">Stored encrypted. e.g. group <code>payment</code>, key <code>api_key</code>.</p>
            <div className="space-y-2">
              {creds.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="group" value={c.group} onChange={(e) => setCreds((p) => p.map((x, idx) => idx === i ? { ...x, group: e.target.value } : x))} className="w-28" />
                  <Input placeholder="key" value={c.key} onChange={(e) => setCreds((p) => p.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))} className="w-32" />
                  <Input placeholder="value" value={c.value} onChange={(e) => setCreds((p) => p.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} />
                  <Button size="icon" variant="ghost" onClick={() => setCreds((p) => p.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setCreds((p) => [...p, { group: '', key: '', value: '' }])}><Plus className="mr-2 h-4 w-4" />Add credential</Button>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={next}>Skip</Button>
              <Button disabled={savingInt} onClick={() => void submitIntegrations()}>{savingInt ? 'Saving…' : 'Save & continue'}</Button>
            </div>
          </div>
        )}

        {step === 'finish' && (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="h-6 w-6" /></div>
            <h3 className="font-medium">You're all set</h3>
            <p className="text-sm text-muted-foreground">You can change branding and integrations later in Settings.</p>
            <Button className="w-full" disabled={completing} onClick={() => void finish()}>{completing ? 'Finishing…' : 'Go to dashboard'}</Button>
          </div>
        )}
      </div>
    </div>
  )
}
